"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { AppLayout } from "@/components/Layout/AppLayout";
import { MapProvider } from "@/features/map/MapContext";
import type { AppLocale } from "@/i18n/routing";

/**
 * Client boundary for the map and its chrome.
 *
 * `ssr: false` is required, not a preference: mapbox-gl reaches for `window` and a
 * WebGL context at import time. Keeping the boundary in its own module means the
 * page above it stays a server component, so the tenant's title, theme and
 * `<html lang>` are still resolved before the first byte.
 *
 * The shell is loaded the same way. It reads the map through context, so rendering
 * it server-side would only produce chrome that can't talk to anything.
 */
const Map = dynamic(() => import("@/components/Map/Map"), {
  ssr: false,
  loading: () => <div className="h-full w-full animate-pulse bg-gray-100" />,
});

export function MapView({
  locales,
  children,
}: {
  locales: AppLocale[];
  children?: ReactNode;
}) {
  return (
    // The provider sits above both, not inside the map boundary: the shell talks
    // to the map too — the measurement tool draws on it — and a provider nested
    // in the canvas would leave every hook in the sidebar without one.
    <MapProvider>
      <Map />
      <AppLayout locales={locales}>{children}</AppLayout>
    </MapProvider>
  );
}
